'use client';

import clsx from 'clsx';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import PageBreadcrumbs from '@/components/PageBreadcrumbs';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import useDevice from '@/hooks/useDevice';
import { useViewportVideosPlayback } from '@/hooks/useViewportVideosPlayback';
import { breadcrumbTrails } from '@/lib/breadcrumbs';
import { gsap } from '@/lib/gsap';

import css from './style.module.scss';
import { AboutUsHeroDto } from './types';

interface Props {
	data: AboutUsHeroDto;
}

const AboutUsHero = ({ data }: Props) => {
	const device = useDevice();

	const [isMuted, setIsMuted] = useState(true);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const videoRef = useRef<HTMLVideoElement>(null);
	const breadcrumbsRef = useRef<HTMLDivElement>(null);
	const soundBtnLabel = isMuted ? data.soundOnLabel : data.soundOffLabel;

	useViewportVideosPlayback([wrapperRef]);

	useLayoutEffect(() => {
		const wrapper = wrapperRef.current;
		const video = videoRef.current;

		if (!wrapper || !video) return;

		const breadcrumbs = breadcrumbsRef.current;
		const titleLines = wrapper.querySelectorAll(`.${css.title} p`);
		const bottom = wrapper.querySelector(`.${css.bottomContainer}`);

		const ctx = gsap.context(() => {
			const tl = gsap.timeline({
				defaults: { ease: 'power3.out', duration: 1.2 },
				scrollTrigger: {
					trigger: wrapper,
					start: 'top 80%',
					toggleActions: 'play none none reverse'
				}
			});

			gsap.set(video, { opacity: 0, scale: 1.1 });
			gsap.set([titleLines, bottom], {
				opacity: 0,
				y: 40,
				filter: 'blur(10px)'
			});

			if (breadcrumbs)
				gsap.set(breadcrumbs, {
					opacity: 0,
					y: 40,
					filter: 'blur(10px)'
				});

			tl.to(video, {
				opacity: 1,
				scale: 1,
				duration: 1.8,
				ease: 'power2.out'
			})
				.to(
					titleLines,
					{
						opacity: 1,
						y: 0,
						filter: 'blur(0px)',
						stagger: 0.15,
						duration: 1.1,
						ease: 'power3.out'
					},
					'-=1'
				)
				.to(
					bottom,
					{
						opacity: 1,
						y: 0,
						filter: 'blur(0px)',
						duration: 1,
						ease: 'power2.out'
					},
					'-=0.6'
				);

			if (breadcrumbs)
				tl.to(
					breadcrumbs,
					{
						opacity: 1,
						y: 0,
						filter: 'blur(0px)',
						duration: 1,
						ease: 'power2.out'
					},
					'-=0.6'
				);
		}, wrapper);

		return () => ctx.revert();
	}, []);

	useEffect(() => {
		const video = videoRef.current;
		if (!video) return;

		video.load();
		video.play().catch(() => {});
	}, [device]);

	return (
		<section ref={wrapperRef} className={clsx('wrapper', css.wrapper)}>
			<video
				ref={videoRef}
				autoPlay
				loop
				playsInline
				muted={isMuted}
				controls={false}
				className={css.video}
				preload="none"
				poster="/assets/images/about-us-page/hero-poster.webp"
			>
				{device === 'desktop' ? (
					<>
						<source src="/assets/video/webm/about-us-hero.webm" type="video/webm" />
						<source src="/assets/video/mp4/about-us-hero.mp4" type="video/mp4" />
					</>
				) : (
					<>
						<source src="/assets/video/webm/about-us-hero-mobile.webm" type="video/webm" />
						<source src="/assets/video/mp4/about-us-hero-mobile.mp4" type="video/mp4" />
					</>
				)}
			</video>

			<div className={css.content}>
				<PageBreadcrumbs
					ref={breadcrumbsRef}
					wrapperClassName={css.breadcrumbsWrap}
					className={css.breadcrumbs}
					items={breadcrumbTrails.aboutUs()}
				/>
				<Typography uppercase className={css.title} variant="h1" tag="h1">
					<StrapiRichContent content={data.title} />
				</Typography>
			</div>

			<div className={css.bottomContainer}>
				<p>{data.bottomText}</p>
				<button
					aria-label={soundBtnLabel}
					type="button"
					className={clsx(css.soundBtn, {
						[css.soundActive]: !isMuted
					})}
					onClick={() => setIsMuted(!isMuted)}
				>
					<span className={css.soundBtnLabel}>{soundBtnLabel}</span>
					{Array.from({ length: 5 }, (_, i) => (
						<div key={`wave_${i}`} className={css.soundWave} />
					))}
				</button>
			</div>
		</section>
	);
};

export default AboutUsHero;
